import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageCircle, Mail, MapPin } from "lucide-react";
import { CONTACT, IMAGES } from "@/data";
import { Reveal, SectionLabel } from "@/components/site/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { full_name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [source, setSource] = useState("contact");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { source: src, subject } = e.detail || {};
      setSource(src || "contact");
      if (subject) setForm((f) => ({ ...f, subject: `Consultation — ${subject}` }));
    };
    window.addEventListener("inquiry:open", handler);
    return () => window.removeEventListener("inquiry:open", handler);
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source });
      toast.success("Thank you — your message has been received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const isConsult = source === "consultation";

  const inputCls =
    "w-full bg-transparent border-b border-black/20 py-3 text-charcoal placeholder:text-muted-foreground/60 focus:outline-none focus:border-crimson transition-colors duration-200";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-36 bg-[#F5F5F7] text-charcoal relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel className="!text-crimson">
              {isConsult ? "Book a Consultation" : "Contact"}
            </SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-heading font-bold text-4xl lg:text-6xl tracking-tight leading-[1.02]">
              {isConsult ? "Let's plan your path forward." : "Get in touch."}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Tell us a little about your situation and we&rsquo;ll respond with honest,
              personalized guidance.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-12 space-y-6">
              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 group"
              >
                <span className="w-12 h-12 flex items-center justify-center border border-black/15 group-hover:border-crimson group-hover:bg-crimson group-hover:text-white transition-colors">
                  <MessageCircle size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</span>
                  <span className="block text-lg">{CONTACT.whatsapp}</span>
                </span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <span className="w-12 h-12 flex items-center justify-center border border-black/15 group-hover:border-crimson group-hover:bg-crimson group-hover:text-white transition-colors">
                  <Mail size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</span>
                  <span className="block text-lg">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 flex items-center justify-center border border-black/15">
                  <MapPin size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.2em] text-muted-foreground">Location</span>
                  <span className="block text-lg">{CONTACT.location}</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Full Name</label>
                  <input
                    data-testid="input-full-name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email Address</label>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Subject</label>
                <input
                  data-testid="input-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="How can we help?"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Message</label>
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Tell us about your situation..."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <motion.button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-crimson hover:bg-crimson-dark text-white px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : isConsult ? "Request Consultation" : "Send Message"}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
